"use client";
import ProductImage from "@/components/layout/ProductImage";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CartContext } from "@/context/CartContext";
import { trimText } from "@/lib/trimText";
import { formatCurrency } from "@/utils/formatCurrency";
import { ShoppingBag } from "lucide-react";
import Link from "next/link";
import { useContext, useEffect, useState } from "react";
import AddWishlist from "./AddWishlist";
import Navigations from "./Navigations";
import RemoveFromCart from "./RemoveFromCart";
import UpdateCart from "./UpdateCart";

const CartTable = () => {
  const { cart, getTotalPrice } = useContext(CartContext);
  const [cartProducts, setCartProducts] = useState([]);
  const [cartTotalPrice, setCartTotalPrice] = useState(0);

  useEffect(() => {
    setCartProducts(cart);
    setCartTotalPrice(getTotalPrice());
  }, [cart]);

  useEffect(() => {
    const cartItems = cart.map((item) => ({
      item_id: item?._id,
      item_name: item?.title,
      item_brand: item?.brand || "",
      item_category: item?.category,
      item_subcategory: item?.subCategory,
      item_type: item?.type || item?.brand,
      item_variant: item?.variant || "",
      price: item?.discountedPrice,
      quantity: item?.quantity,
    }));

    const totalValue = getTotalPrice();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });

    window.dataLayer.push({
      event: "view_cart",
      ecommerce: {
        value: totalValue,
        currency: "BDT",
        items: cartItems,
      },
    });
  }, [cart, getTotalPrice]);

  if (!cartProducts || cartProducts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <ShoppingBag className="text-gray-400" size={28} />
        </div>
        <p className="text-gray-800 text-lg font-medium">Your cart is empty</p>
        <p className="text-gray-500 text-sm mt-1">Add items to get started.</p>
        <Link
          href="/"
          className="inline-block mt-6 px-6 py-2.5 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-800 transition"
        >
          Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="flex lg:flex-row flex-col gap-6 items-start">
      <Card className="w-full border-gray-200 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-semibold">
            Your Cart{" "}
            <span className="text-gray-400 font-normal">
              ({cartProducts.length} {cartProducts.length > 1 ? "items" : "item"})
            </span>
          </CardTitle>
        </CardHeader>
        <Separator />

        {/* Desktop table */}
        <CardContent className="mt-2 hidden md:block">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="py-3 pr-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Product
                </th>
                <th className="py-3 px-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Qty
                </th>
                <th className="py-3 px-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Price
                </th>
                <th className="py-3 pl-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cartProducts.map((data, index) => (
                <tr key={index} className="align-top">
                  <td className="py-4 pr-2">
                    <div className="flex gap-4">
                      <Link
                        href={`/${data?.slug}`}

                        className="w-16 h-16 p-1.5 shrink-0 rounded-md border border-gray-100 bg-gray-50 overflow-hidden"
                      >
                        <ProductImage product={data} />
                      </Link>
                      <div className="flex flex-col gap-2 min-w-0">
                        <div className="flex flex-col min-w-0">
                          <Link
                            href={`/${data?.slug}`}

                            className="text-sm font-medium text-gray-900 hover:text-blue-900 transition truncate"
                          >
                            {trimText(data?.title, 30)}
                          </Link>
                          {data?.category?.toLowerCase() !== "stationary" && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {data?.author?.name ? (
                                <>
                                  By{" "}
                                  <span className="text-blue-900 capitalize">
                                    {data?.author?.name}
                                  </span>
                                </>
                              ) : (
                                <>
                                  Brand{" "}
                                  <span className="text-blue-900 capitalize">
                                    {data?.brand}
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-4 items-center">
                          <AddWishlist data={data} />
                          <RemoveFromCart data={data} />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-2 font-medium align-middle">
                    <UpdateCart data={data} />
                  </td>
                  <td className="py-4 px-2 text-sm text-gray-700 align-middle whitespace-nowrap">
                    {formatCurrency(Math.round(data?.discountedPrice))}
                  </td>
                  <td className="py-4 pl-2 text-sm font-semibold text-gray-900 align-middle text-right whitespace-nowrap">
                    {formatCurrency(
                      Math.round(data?.discountedPrice) * data?.quantity
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>

        {/* Mobile stacked cards */}
        <CardContent className="mt-2 md:hidden space-y-4">
          {cartProducts.map((data, index) => (
            <div
              key={index}
              className="flex gap-3 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0"
            >
              <Link
                href={`/${data?.slug}`}

                className="w-16 h-16 p-1.5 shrink-0 rounded-md border border-gray-100 bg-gray-50 overflow-hidden"
              >
                <ProductImage product={data} />
              </Link>
              <div className="flex flex-col gap-2 min-w-0 flex-1">
                <Link
                  href={`/${data?.slug}`}

                  className="text-sm font-medium text-gray-900 truncate"
                >
                  {trimText(data?.title, 26)}
                </Link>
                {data?.category?.toLowerCase() !== "stationary" && (
                  <div className="text-xs text-gray-500 -mt-1.5">
                    {data?.author?.name ? (
                      <>
                        By{" "}
                        <span className="text-blue-900 capitalize">
                          {data?.author?.name}
                        </span>
                      </>
                    ) : (
                      <>
                        Brand{" "}
                        <span className="text-blue-900 capitalize">
                          {data?.brand}
                        </span>
                      </>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 mt-1">
                  <UpdateCart data={data} />
                  <p className="text-sm font-semibold text-gray-900">
                    {formatCurrency(
                      Math.round(data?.discountedPrice) * data?.quantity
                    )}
                  </p>
                </div>

                <div className="flex gap-4 items-center">
                  <AddWishlist data={data} />
                  <RemoveFromCart data={data} />
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="w-full lg:w-[420px] shrink-0 border-gray-200 shadow-sm lg:sticky lg:top-24">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-semibold">Order Summary</CardTitle>
        </CardHeader>
        <Separator />
        <CardContent className="space-y-3 mt-4">
          <div className="flex items-center justify-between text-sm">
            <p className="text-gray-500">Subtotal</p>
            <p className="font-medium text-gray-900">
              {formatCurrency(cartTotalPrice)}
            </p>
          </div>
          <div className="flex items-center justify-between text-sm">
            <p className="text-gray-500">Shipping</p>
            <p className="font-medium text-gray-500">Calculated in Checkout</p>
          </div>
          <Separator />
          <div className="flex items-center justify-between pt-1">
            <p className="text-base font-semibold text-gray-900">Payable Total</p>
            <p className="text-base font-bold text-gray-900">
              {formatCurrency(cartTotalPrice)}
            </p>
          </div>
        </CardContent>
        <CardFooter>
          <Navigations />
        </CardFooter>
      </Card>
    </div>
  );
};

export default CartTable;